// The unified activity log (milestone 12, merged): changes (who edited,
// published, ran what) and data access (who viewed which rows, API calls)
// in one feed. Changes are visible to any viewer; the access half is
// admin-only. Server-side filters, sorting, pagination and CSV export.
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router'

import { api, can, type ActivityParams, type ActivityRow } from '../api'
import { ErrorBox, Pill } from '../components'
import { download } from '../dashApi'
import { dt } from '../format'
import { ChangeDetail } from './history'

const kinds = ['', 'source', 'bucket', 'endpoint', 'dashboard', 'report', 'raw_file', 'integration', 'build', 'api_key']
const sorts: { value: string; label: string }[] = [
  { value: 'at', label: 'Time' },
  { value: 'actor', label: 'Who' },
  { value: 'action', label: 'Action' },
  { value: 'object', label: 'Object' },
  { value: 'type', label: 'Type' },
]
const PAGE = 50

const emptyFilters = { type: 'all', object_kind: '', object_slug: '', actor: '', key: '', from: '', to: '', all: '' }

export function Audit() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const admin = can(me.data, 'admin')
  // Initial filters can come from the URL (HistoryLink opens an object's changes).
  const [sp] = useSearchParams()
  const [f, setFilters] = useState({
    ...emptyFilters,
    type: sp.get('type') || 'all',
    object_kind: sp.get('object_kind') || '',
    object_slug: sp.get('object_slug') || '',
  })
  const [sort, setSort] = useState('at')
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')
  const [offset, setOffset] = useState(0)
  const [open, setOpen] = useState<number | null>(null)

  // Any filter/sort change resets to the first page.
  const params: ActivityParams = { ...f, sort, dir, limit: PAGE, offset }
  const clean: ActivityParams = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== 'all'))
  const q = useQuery({ queryKey: ['activity', clean], queryFn: () => api.activity(clean) })

  const setF = (patch: Partial<typeof f>) => {
    setFilters({ ...f, ...patch })
    setOffset(0)
    setOpen(null)
  }
  const changeSort = (s: string) => {
    setSort(s)
    setOffset(0)
  }
  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      download(await api.activityCsv({ ...f, sort, dir }), 'audit-log.csv')
    } finally {
      setExporting(false)
    }
  }

  const rows = q.data?.rows ?? []
  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Audit log</h1>
          <p className="sub">
            Changes (who edited, published or ran what) and {admin ? 'data access (who viewed which rows and the keys of the first 100, plus API calls)' : 'your changes'} in one feed.
            {admin && ' The access half is personal data, kept for 12 months and visible to admins only.'}
          </p>
        </div>
        <button onClick={exportCsv} disabled={exporting || q.isLoading}>{exporting ? 'Exporting…' : 'Export CSV'}</button>
      </header>

      <div className="dash-filters">
        {admin && (
          <label className="dash-filter">
            <span className="muted">Type</span>
            <select value={f.type} onChange={(e) => setF({ type: e.target.value })}>
              <option value="all">all</option>
              <option value="change">changes</option>
              <option value="access">access</option>
            </select>
          </label>
        )}
        <label className="dash-filter">
          <span className="muted">Object</span>
          <select value={f.object_kind} onChange={(e) => setF({ object_kind: e.target.value })}>
            {kinds.map((k) => (
              <option key={k} value={k}>{k || 'any'}</option>
            ))}
          </select>
          <input placeholder="slug" className="mono" value={f.object_slug} onChange={(e) => setF({ object_slug: e.target.value })} />
        </label>
        <label className="dash-filter">
          <span className="muted">Who</span>
          <input placeholder="name, email or key" value={f.actor} onChange={(e) => setF({ actor: e.target.value })} />
        </label>
        {admin && (
          <label className="dash-filter">
            <span className="muted">Saw entry</span>
            <input placeholder="e.g. example.se" className="mono" value={f.key} onChange={(e) => setF({ key: e.target.value })} />
          </label>
        )}
        <label className="dash-filter">
          <span className="muted">From</span>
          <input type="date" value={f.from} onChange={(e) => setF({ from: e.target.value })} />
          <span className="muted">to</span>
          <input type="date" value={f.to} onChange={(e) => setF({ to: e.target.value })} />
        </label>
        <label className="dash-filter">
          <span className="muted">Sort</span>
          <select value={sort} onChange={(e) => changeSort(e.target.value)}>
            {sorts.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button onClick={() => { setDir(dir === 'desc' ? 'asc' : 'desc'); setOffset(0) }} title="Toggle direction">{dir === 'desc' ? '↓' : '↑'}</button>
        </label>
        {me.data?.super && (
          <label className="dash-filter">
            <input type="checkbox" checked={f.all === '1'} onChange={(e) => setF({ all: e.target.checked ? '1' : '' })} />
            <span>All organisations</span>
          </label>
        )}
      </div>

      {q.error && <ErrorBox error={q.error} />}
      <table className="tbl">
        <thead>
          <tr>
            <th>When</th>
            <th>Type</th>
            {f.all === '1' && <th>Org</th>}
            <th>Who</th>
            <th>What</th>
            <th>Detail</th>
            <th>Address</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} onClick={() => setOpen(open === i ? null : i)} className="clickable">
              <td>{dt(r.at)}</td>
              <td><Pill kind={r.type === 'change' ? 'info' : 'neutral'}>{r.type}</Pill></td>
              {f.all === '1' && <td className="mono">{r.org_id}</td>}
              <td>{r.actor_kind === 'user' ? r.actor_name : <Pill kind={r.actor_kind === 'anonymous' ? 'neutral' : 'info'}>{r.actor_kind === 'api_key' ? `key ${r.actor_name}` : r.actor_kind}</Pill>}</td>
              <td>
                <span className="mono">{r.object_kind} {r.object_slug}</span> <span className="muted">{r.action}</span>
                {r.version != null && <span className="muted"> v{r.version}</span>}
                {r.status != null && r.status >= 400 && <Pill kind="err">{r.status}</Pill>}
              </td>
              <td>{detail(r, open === i)}</td>
              <td className="mono muted">{r.client_ip}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!q.isLoading && rows.length === 0 && <p className="muted">No entries.</p>}

      <div className="pager">
        <button disabled={offset === 0} onClick={() => { setOffset(Math.max(0, offset - PAGE)); setOpen(null) }}>← Previous</button>
        <span className="muted">{rows.length === 0 ? '0' : `${offset + 1}–${offset + rows.length}`}</span>
        <button disabled={!q.data?.has_more} onClick={() => { setOffset(offset + PAGE); setOpen(null) }}>Next →</button>
      </div>
    </section>
  )
}

function detail(r: ActivityRow, open: boolean) {
  if (r.type === 'access') {
    const keys = r.keys ?? []
    const shown = open ? keys.join(', ') : keys.slice(0, 3).join(', ') + (keys.length > 3 ? ` +${keys.length - 3}` : '')
    return (
      <>
        {r.rows != null && <span>{r.rows} row{r.rows === 1 ? '' : 's'}</span>}
        {keys.length > 0 && <span className="mono muted"> · {shown}</span>}
        {open && r.params && <pre className="mono note">{JSON.stringify(r.params, null, 2)}</pre>}
      </>
    )
  }
  // change: expand to the change-set's diff + undo.
  if (open && r.change_set_id) {
    return <ChangeDetail id={r.change_set_id} />
  }
  const summary = JSON.stringify(r.after ?? {})
  return <span className="mono muted">{summary === '{}' ? '–' : summary.slice(0, 80)}</span>
}
